/**
 * BP-01:长图阅读器(全屏接管)。
 *
 * ★**为什么不能复用 MediaPreviewLightbox**:那个是「暗底 + object-contain」范式,
 * contain 一张 2160×9727 的图只会把它缩得**更小**(受视口高度钳制),比不点开还糟。
 * 长图的读法是**纵向连读**不是整体观看 —— 必须宽度对齐视口、纵向滚动、按屏翻页。
 *
 * 现状(改之前):成品整图被塞进 `max-h-[70vh]` 滚动盒 + `<img class="w-full">`,
 * 实测可视 504px / 图高 4161px → 要滚 8.3 屏,且缩到 **43%**,原稿 14px 正文到眼前只剩 6px,
 * 不可点、无放大、无查看原图。**长图的全部价值就是上面的字要能读。**
 *
 * 三档:适宽(默认,铺满视口宽)/ 100%(原始像素,横向可拖)/ 按屏翻页。
 * Esc 关 · ←→ 翻屏 · 关闭后焦点回到触发元素(a11y:不能把焦点丢给 body)。
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, X } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";
import { screenCount } from "../lib/screenCount";

export type ReaderZoom = "fit" | "actual";

export interface LongImageReaderProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  url: string;
  title?: string;
  /** 原始像素(用于「100%」档与屏数提示);缺省则不显示屏数。 */
  width?: number | null;
  height?: number | null;
  /**
   * D-01(琳琳「点开查看大图很卡,一直转圈」):已经下载过的缩览图 URL。
   *
   * 原图是 1500×8752 级,传输 + 解码期间这一层原本什么都没有 —— 用户只能盯着空屏
   * 猜是不是坏了。给了缩览就先铺缩览(同一版式,位置不跳)+ 明说「原图加载中」,
   * 原图到了再换。没有缩览就照旧(不编一个不存在的图)。
   */
  placeholderUrl?: string | null;
  onDownload?: () => void;
  downloading?: boolean;
}

export function LongImageReader({
  open,
  onOpenChange,
  url,
  title,
  width,
  height,
  onDownload,
  downloading,
  placeholderUrl,
}: LongImageReaderProps) {
  const [zoom, setZoom] = useState<ReaderZoom>("fit");
  const [measuredScreens, setMeasuredScreens] = useState<number | null>(null);
  // D-01:原图到位前先铺缩览。换 url(切另一张图)时要重新回到「加载中」。
  const [fullLoaded, setFullLoaded] = useState(false);
  useEffect(() => {
    setFullLoaded(false);
  }, [url]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // 关闭后把焦点还给触发元素(Radix 受控 Dialog 无 Trigger 会把焦点丢给 body,这里是原生实现,自己还)
  const restoreRef = useRef<HTMLElement | null>(null);

  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      restoreRef.current = document.activeElement as HTMLElement | null;
      // ★codex P2:声明了 aria-modal 就必须真把焦点收进来 —— 否则 Tab 会继续走到
      // 被全屏盖住的背景元素上,屏幕阅读器用户完全不知道自己在操作什么。
      dialogRef.current?.focus?.();
    } else {
      restoreRef.current?.focus?.();
    }
  }, [open]);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !el.clientHeight) return;
    setMeasuredScreens(screenCount(el.scrollHeight, el.clientHeight));
  }, []);

  useEffect(() => {
    if (!open) return;
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open, zoom, measure]);

  const pageBy = useCallback((dir: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    // 「按屏翻页」= 翻一个视口高度,留 8% 重叠让读者接得上上一屏的末行
    el.scrollBy({ top: dir * el.clientHeight * 0.92, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        // 焦点陷阱:只在阅读器内部循环(首尾相接)
        const root = dialogRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (!root.contains(active)) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      if (e.key === "Escape") onOpenChange(false);
      else if (e.key === "ArrowRight" || e.key === "PageDown") {
        e.preventDefault();
        pageBy(1);
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        pageBy(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    // 全屏接管时锁 body 滚动,否则背后页面跟着滚
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onOpenChange, pageBy]);

  if (!open) return null;

  // ★codex P3:适宽档下图被缩到容器宽,真实滚动高度远小于原始像素高 ——
  // 拿 9727px 报屏数会把 8 屏说成 13 屏。用滚动容器的真实 scrollHeight 量,
  // 两个档位都准(100% 档同样自动对)。量不到就不显示,不猜。
  const screens = measuredScreens;

  // ★必须 portal 到 body:`position:fixed` 会相对**有 transform/filter 的祖先**定位,
  // 而不是视口 —— 布局里只要有一层 transform(动画/皮肤都会加),「全屏接管」就变成
  // 「在主内容区里接管」。e2e 实测:不 portal 时阅读器只有 972px 宽(视口 1280,被侧边栏挤掉)。
  return createPortal(
    <div
      ref={dialogRef}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col bg-background outline-none"
      role="dialog"
      aria-modal="true"
      aria-label={title ?? "长图阅读器"}
      // ★皮肤例外钩子(别删):三套皮肤的 `[data-skin="x"] [role="dialog"]` 射程是整棵文档树
      //   (data-skin 挂在 <html>),portal 出去的全屏层照样中招。cyber 会把这层刷成深蓝渐变,
      //   而工具条那几颗 ghost 按钮(含关闭键)的字色仍走 --foreground(深色)→ 对比度 1.12,
      //   关闭键隐形 —— 与 2026-08-05 用户报障的看图层是同一个坑。
      //   这里不能用 data-skin-plain:那条把背景刷成 transparent,全屏层会漏出底下内容。
      data-fullscreen-surface
      data-testid="long-image-reader"
    >
      <div className="flex shrink-0 items-center gap-2 border-b px-4 py-2">
        <span className="truncate text-sm font-medium">{title ?? "成品整图"}</span>
        <span className="text-xs text-muted-foreground">
          {width && height ? `${width}×${height}` : ""}
          {screens ? ` · 约 ${screens} 屏` : ""}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <Button
            size="sm"
            variant={zoom === "fit" ? "default" : "ghost"}
            onClick={() => setZoom("fit")}
            data-testid="reader-zoom-fit"
          >
            适宽
          </Button>
          <Button
            size="sm"
            variant={zoom === "actual" ? "default" : "ghost"}
            onClick={() => setZoom("actual")}
            data-testid="reader-zoom-actual"
          >
            100%
          </Button>
          {onDownload && (
            <Button size="sm" variant="ghost" onClick={onDownload} disabled={downloading}>
              <Download className="mr-1.5 h-4 w-4" />
              {downloading ? "准备中…" : "下载原图"}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            aria-label="关闭阅读器"
            onClick={() => onOpenChange(false)}
            data-testid="reader-close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-auto bg-muted/30"
        data-testid="reader-scroll"
        role="region"
        aria-label="长图内容,可上下滚动"
        // 可滚动区按 WCAG 2.1.1 必须键盘可达(纯键盘用户要能 ↑↓ 细滚);
        // 规则不认 role="region" 为 tabbable,是已知误报。已配 role + aria-label,
        // 不是给裸 div 挂 tabIndex 的那种反模式。
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
      >
        {/* D-01:原图没到之前先铺缩览(同一版式,原图到位不跳位)+ 一句人话说明在等什么。
            缩览本来就已经下载过(列表/面板用的是它),等于零额外流量。 */}
        {placeholderUrl && !fullLoaded && (
          <>
            <img
              src={placeholderUrl}
              alt=""
              aria-hidden
              data-testid="reader-placeholder"
              className="block w-full"
            />
            <div
              data-testid="reader-loading-hint"
              className="pointer-events-none absolute inset-x-0 top-1/2 mx-auto w-max -translate-y-1/2 rounded-full bg-background/90 px-3 py-1.5 text-xs text-muted-foreground shadow"
            >
              原图加载中(高清整图较大,先看缩览)…
            </div>
          </>
        )}
        {/* 适宽:宽度铺满视口(不再被面板宽度压);100%:原始像素,横向可拖 */}
        <img
          src={url}
          alt={title ?? "成品整图"}
          className={cn(
            zoom === "fit" ? "block w-full" : "block max-w-none",
            // 占位在场时把原图收起来,别让半张图闪一下再跳位
            placeholderUrl && !fullLoaded && "hidden"
          )}
          style={zoom === "actual" && width ? { width: `${width}px` } : undefined}
          onLoad={() => {
            setFullLoaded(true);
            measure();
          }}
          decoding="async" // D-01:2160×9727 级整图同步解码会卡死打开动画,异步解码不挡合成
        />
      </div>

      <div className="shrink-0 border-t px-4 py-1.5 text-center text-2xs text-muted-foreground">
        Esc 关闭 · ← → 翻屏 · 拖动滚动条连读
      </div>
    </div>,
    document.body
  );
}
